/**
 * Website Builder types.
 *
 * Derived from backend/apexos/apps/website_builder/serializers.py and
 * views.py (get_status, readiness, style_options, component_options).
 * Keep these in sync with the backend — do not invent fields.
 */

/** WebsiteProject.STATUS_CHOICES (models.py). */
export type WebsiteProjectStatusValue =
  | 'questionnaire'
  | 'generating_design_system'
  | 'generating_homepage'
  | 'homepage_review'
  | 'generating_site'
  | 'generating_content'
  | 'assembling'
  | 'site_review'
  | 'deploying'
  | 'deployed'
  | 'failed';

/** Statuses during which the backend rejects new generation dispatches (409). */
export const WEBSITE_GENERATING_STATUSES: WebsiteProjectStatusValue[] = [
  'generating_design_system',
  'generating_homepage',
  'generating_site',
  'generating_content',
  'assembling',
  'deploying',
];

/** WebsitePage.STATUS_CHOICES (models.py). */
export type WebsitePageStatusValue =
  | 'pending'
  | 'generating'
  | 'generated'
  | 'approved'
  | 'failed';

export type DomainStatus = '' | 'pending_dns' | 'attaching' | 'live' | 'failed';

/** A DNS record the customer must create at their DNS provider. */
export interface DnsRecord {
  type: string;
  host: string;
  value: string;
  purpose: string;
}

/** WebsiteProjectSerializer (list serializer). */
export interface WebsiteProject {
  id: number;
  name: string;
  status: WebsiteProjectStatusValue;
  local_evidence?: Record<string, string>;
  style_preferences: Record<string, string>;
  custom_instructions: string;
  preview_url: string;
  production_url: string;
  revision_count: number;
  pages_count: number;
  generation_task_id: string;
  last_error: string;
  chrome_version: number;
  /** Decimal serialized as string, e.g. "1.42". */
  generation_cost_usd: string;
  custom_domain: string;
  google_analytics_id: string;
  search_console_verification: string;
  domain_status: DomainStatus;
  domain_dns_records: DnsRecord[];
  domain_error: string;
  /** When the whole site last deployed to production; null = never. */
  published_at: string | null;
  created_at: string;
  updated_at: string;
}

/** WebsitePageListSerializer (no HTML content). */
export interface WebsitePageSummary {
  id: number;
  page_type: string;
  /** Legal pages (privacy_policy/terms) block publishing until approved. */
  legal_approval_valid?: boolean;
  title: string;
  slug: string;
  meta_title: string;
  status: WebsitePageStatusValue;
  service_name: string;
  area_city: string;
  area_state: string;
  order: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  component_config: Record<string, any>;
  /** Derived from the shared header/footer HTML: is this page linked there? */
  in_header_nav: boolean;
  in_footer_nav: boolean;
  /** Presigned S3 URL; null until the page is generated. */
  preview_url: string | null;
  /** When this page's html last reached the live site; null = never. */
  published_at: string | null;
  /** The HTML tab was hand-edited; assembler regeneration would discard it. */
  html_manually_edited: boolean;
  /** False for pages generated before content_json was stored — they have no
   * structured source to anchor edits to, so the visual editor is disabled. */
  has_structured_content?: boolean;
  updated_at: string;
}

/** WebsitePageSerializer (full page with HTML). */
export interface WebsitePageDetail {
  id: number;
  page_type: string;
  title: string;
  slug: string;
  html: string;
  css: string;
  meta_title: string;
  meta_description: string;
  /** JSONField — object when generated, may be a string after manual edits. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  json_ld: Record<string, any> | string;
  status: WebsitePageStatusValue;
  service_name: string;
  area_city: string;
  area_state: string;
  order: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  component_config: Record<string, any>;
  /** Owner's brief for AI-written custom pages (empty for generated pages). */
  generation_brief: string;
  /** Derived from the shared header/footer HTML: is this page linked there? */
  in_header_nav: boolean;
  in_footer_nav: boolean;
  /** Legal pages only: consent trail for the stubbed content. */
  legal_approved_at: string | null;
  legal_approval_valid: boolean;
  legal_approved_by_name: string;
  published_at: string | null;
  html_manually_edited: boolean;
  created_at: string;
  updated_at: string;
}

/** Page types whose content must be approved before publishing. */
export const LEGAL_PAGE_TYPES = ['privacy_policy', 'terms'];

/** One item in validation_report.errors / .warnings (services/site_validator.py). */
export interface WebsiteValidationIssue {
  page: string;
  check: string;
  message: string;
}

/** validation_report JSON ({checked_at, passed, errors, warnings}); {} until first validation. */
export interface WebsiteValidationReport {
  checked_at: string;
  passed: boolean;
  errors: WebsiteValidationIssue[];
  warnings: WebsiteValidationIssue[];
}

/** Per-site business-data overrides (backend validates keys). */
export interface WebsiteBusinessOverrides {
  name?: string;
  phone?: string;
  email?: string;
  cta_text?: string;
}

/** Live business data resolved from org settings + per-site overrides. */
export interface WebsiteLiveBusinessInfo {
  name: string;
  phone: string;
  email: string;
  cta_text: string;
}

/** WebsiteProjectDetailSerializer. */
export interface WebsiteProjectDetail extends WebsiteProject {
  homepage_html: string;
  homepage_css: string;
  homepage_js: string;
  shared_header_html: string;
  shared_footer_html: string;
  child_hero_html: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  design_system: Record<string, any>;
  /** Page type → HTML template with {{placeholder}} syntax. {} for legacy projects. */
  page_templates: Record<string, string>;
  generation_method: 'template' | 'legacy';
  pages: WebsitePageSummary[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  knowledge_graph_snapshot: Record<string, any>;
  /** {} until the first pre-deploy validation runs. */
  validation_report: Partial<WebsiteValidationReport>;
  business_overrides: WebsiteBusinessOverrides;
  live_business_info: WebsiteLiveBusinessInfo;
  /** false = the LIVE site carries a stale phone number (offer Update & Publish). */
  business_in_sync: boolean;
}

export interface WebsitePagesProgress {
  total: number;
  completed: number;
  failed: number;
  pending: number;
  percentage: number;
}

/** Per-page entry in the /status/ payload (values() query in get_status). */
export interface WebsiteStatusPage {
  id: number;
  title: string;
  status: WebsitePageStatusValue;
  page_type: string;
  slug: string;
}

/** Live Message-Batch progress while the content phase runs. */
export interface WebsiteBatchProgress {
  total_requests: number;
  succeeded: number;
  errored: number;
  processing: number;
}

/** GET /api/website-builder/projects/{id}/status/ payload. */
export interface WebsiteProjectStatus {
  status: WebsiteProjectStatusValue;
  preview_url: string;
  production_url: string;
  task_id: string;
  revision_count: number;
  last_error: string;
  chrome_version: number;
  generation_cost_usd: string;
  validation_passed: boolean | null;
  /** Celery task state; only present while a task is active. */
  task_status?: string;
  /** Celery failure detail; only present when the active task failed. */
  error?: string;
  pages_progress?: WebsitePagesProgress;
  pages?: WebsiteStatusPage[];
  /** Present only while the content batch is in flight. */
  batch_progress?: WebsiteBatchProgress;
}

/** One pre-flight check from GET /api/website-builder/projects/readiness/. */
export interface ReadinessCheck {
  key: string;
  ok: boolean;
  label: string;
  message: string;
  fix_url: string;
  required: boolean;
  count?: number;
}

export interface ReadinessResponse {
  /** false when at least one REQUIRED check failed. */
  ready: boolean;
  checks: ReadinessCheck[];
}

/**
 * A freshly published site can take up to ~an hour to come online everywhere
 * (hosting + CDN DNS propagation). Used to show a "just published?" note so
 * an early access error isn't mistaken for a broken deploy.
 */
export function isRecentlyDeployed(updatedAt: string | undefined, windowMinutes = 60): boolean {
  if (!updatedAt) return false;
  const updated = new Date(updatedAt).getTime();
  if (Number.isNaN(updated)) return false;
  return Date.now() - updated < windowMinutes * 60 * 1000;
}

export interface WebsiteStyleOption {
  value: string;
  label: string;
  description: string;
}

/** GET /api/website-builder/projects/style-options/. */
export interface WebsiteStyleOptions {
  design_families?: WebsiteStyleOption[];
  layout_styles: WebsiteStyleOption[];
  hero_styles: WebsiteStyleOption[];
  color_moods: WebsiteStyleOption[];
}

/** GET /api/website-builder/projects/{id}/component-options/. */
export interface WebsiteComponentOptions {
  forms: Array<{ id: number; embed_id: string; name: string }>;
  review_widgets: Array<{ id: number; uuid_public: string; name: string }>;
  photos: Array<{
    id?: number;
    url: string;
    /** Smaller rendition for picker grids; falls back to url server-side. */
    thumbnail_url?: string;
    name: string;
    alt_text?: string;
  }>;
}

/** 202 body for generate-homepage / generate-site / deploy / revise / republish-chrome. */
export interface WebsiteTaskDispatchResponse {
  status: string;
  task_id: string;
  message: string;
}

/** WebsiteRevision.STATUS_CHOICES — lifecycle of one AI edit. */
export type WebsiteRevisionStatus = 'pending' | 'running' | 'succeeded' | 'failed' | 'no_change';

/** WebsiteRevision.SCOPE_CHOICES — what the edit touched ('' = pre-scope rows). */
export type WebsiteRevisionScope = '' | 'page_content' | 'chrome' | 'page_template' | 'business_data';

/** WebsiteRevisionSerializer (list + detail poll). */
export interface WebsiteRevisionInfo {
  id: number;
  page: number | null;
  page_title: string | null;
  feedback: string;
  revision_number: number;
  status: WebsiteRevisionStatus;
  scope: WebsiteRevisionScope;
  error: string;
  result_summary: string;
  created_at: string;
}

/** 202 body for POST .../ai-edit/. */
export interface WebsiteAiEditDispatchResponse {
  status: string;
  revision_id: number;
  task_id: string;
  message: string;
}

/** POST /api/website-builder/projects/ body (CreateProjectSerializer). */
export interface WebsiteProjectCreatePayload {
  name?: string;
  local_evidence?: Record<string, string>;
  style_preferences: Record<string, string>;
  custom_instructions?: string;
}

/** PATCH /api/website-builder/projects/{id}/ body (writable detail fields). */
export interface WebsiteProjectUpdatePayload {
  google_analytics_id?: string;
  search_console_verification?: string;
  name?: string;
  local_evidence?: Record<string, string>;
  style_preferences?: Record<string, string>;
  custom_instructions?: string;
  homepage_html?: string;
  homepage_css?: string;
  shared_header_html?: string;
  shared_footer_html?: string;
  child_hero_html?: string;
  page_templates?: Record<string, string>;
  business_overrides?: WebsiteBusinessOverrides;
}

/** POST .../pages/ body (WebsitePageCreateSerializer). */
export interface WebsitePageCreatePayload {
  /** "custom" = AI writes the page from `brief`; "reviews" = standard Customer Reviews page. */
  page_type?: 'custom' | 'reviews';
  title?: string;
  /** Lowercase letters, numbers, hyphens; derived from the title when omitted. */
  slug?: string;
  brief?: string;
  show_in_header?: boolean;
  show_in_footer?: boolean;
}

export interface WebsitePageCreateResponse {
  page: WebsitePageDetail;
  /** Header/footer links were requested; they are added once the page has content. */
  nav_requested: boolean;
  message: string;
}

/** PATCH .../pages/{pageId}/update/ response: the page plus whether the shared chrome changed. */
export interface WebsitePageUpdateResponse extends WebsitePageDetail {
  nav_changed: boolean;
}

/** PATCH .../pages/{pageId}/update/ body (WebsitePageUpdateSerializer). */
export interface WebsitePageUpdatePayload {
  base_updated_at: string;
  html?: string;
  css?: string;
  title?: string;
  generation_brief?: string;
  /** Add/remove this page's link in the shared header / footer. */
  show_in_header?: boolean;
  show_in_footer?: boolean;
  meta_title?: string;
  meta_description?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  json_ld?: Record<string, any> | string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  component_config?: Record<string, any>;
}

// ---------------------------------------------------------------------------
// Visual Editor (GET .../editor-document/, POST .../visual-edit/)
// ---------------------------------------------------------------------------

/** content_json keys the visual editor may read/write. */
export type EditorFieldKey =
  | 'hero_heading'
  | 'hero_subheading'
  | 'page_title'
  | 'breadcrumb'
  | 'content_sections'
  | 'faq_items';

export interface EditorStyleVocab {
  section_classes: string[];
  button_classes: string[];
  text_classes: string[];
}

export interface EditorPageMeta {
  id: number;
  title: string;
  slug: string;
  page_type: string;
  status: string;
  updated_at: string;
  published_at: string | null;
  html_manually_edited: boolean;
  is_legal: boolean;
}


/** A stored source the page is assembled from (chrome or layout). */
export type EditorRegionName = 'header' | 'footer' | 'template';

export interface EditorRegion {
  name: EditorRegionName;
  editable: boolean;
  /** Owner-facing explanation when not editable. */
  reason: string;
  affected_pages: number;
  scope: 'page' | 'shared' | 'site';
}

export interface EditorDocumentResponse {
  chrome_version?: number;
  html: string;
  page: EditorPageMeta;
  editable_keys: EditorFieldKey[];
  style_vocab: EditorStyleVocab;
  design: {
    /** The site's generated palette. */
    colors: string[];
    /** The organization's own brand colours (Settings → Organization). */
    brand_colors: string[];
    typography: { headings_font: string; body_font: string };
    /** The organization's brand typefaces. */
    brand_typefaces: string[];
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  component_config: Record<string, any>;
  regions: EditorRegion[];
  serializer_profile: { tags: string[]; attrs: string[] };
}

export interface VisualEditPayload {
  content?: Partial<Record<EditorFieldKey, string>>;
  /** Edited layout/chrome sources, keyed by region name. */
  regions?: Partial<Record<EditorRegionName, string>>;
  base_chrome_version?: number | null;
  publish_affected?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  component_config?: Record<string, any>;
  removed_markers?: string[];
  base_updated_at: string;
  force?: boolean;
  summary?: string;
}

export interface VisualEditResponse {
  page: WebsitePageDetail;
  revision_id: number;
  html: string;
  affected_page_ids: number[];
  published_page_ids: number[];
  chrome_version: number;
}

/** What the bridge reports when the user clicks something in the canvas. */
export type EditorSelectionKind =
  | 'text'
  | 'heroField'
  | 'image'
  | 'button'
  | 'section'
  | 'locked'
  | 'chrome'
  | 'template'
  | 'page';

export interface EditorSelection {
  kind: EditorSelectionKind;
  /** Which stored source this element lives in (undefined => page content). */
  regionName?: EditorRegionName;
  /** Which editable field the element sits in (undefined => outside all). */
  fieldKey?: EditorFieldKey;
  /** Locked component marker text (data-apexos-restore), verbatim. */
  marker?: string;
  /** Image slot ("hero", "work:1") when kind === 'image'. */
  slot?: string;
  meta: {
    tag: string;
    classes: string[];
    inlineStyle: string;
    href?: string;
    text?: string;
    src?: string;
    alt?: string;
    lockedKind?: string;
  };
}
